/**
 * P3-04: 键盘切 tab——数字键与左右方向键（平移自 js/ui.js:296-335 的 keyup 分支）。
 *
 * 平移范围（对照 js/ui.js:179-346）：
 * - 数字键切 tab：keyCode 48-57（数字行）/ 96-105（小键盘），tabIndex 缺省 9，
 *   小键盘 97+ → keyCode-97，数字行 49-57 → keyCode-49（js/ui.js:301-317）；
 * - 左右方向键（37/39）：按 visible tab 序列 ±1 步进，越界不动（js/ui.js:318-335）；
 * - isInputElement 排除：TEXTAREA 与 INPUT[type=text/number/password/email]
 *   （js/ui.js:296-300；checkbox 等其余类型**不排除**，与旧版同语义）。
 *
 * 不在本层（保持旧层直至 P4 及以后）：
 * - Escape 关对话框（keybind.action，js/ui.js:264-271/304-306）；
 * - Shift+字母切 tab（keybinds 表，js/ui.js:186-272/336-344）。
 *
 * 单一绑定保证：旧层两个分支已加 `!kgNewUiTabKeysActive()` 守卫（js/ui.js
 * constructor keyup 段，见该处 TODO(ui-refactor P3-04)），本层 enable() 后旧
 * 分支跳过；enable() 自身幂等（bound 标志），disable() 摘除后旧路径原样恢复。
 * 修饰键语义与旧版一致：数字/方向分支不检查 shift/ctrl/alt。
 *
 * 绑定目标：旧版 dojo.connect($("html")[0], "onkeyup")；本层绑 window keyup
 * （事件冒泡链路 html→document→window，target 一致，效果等价）。
 */
import { getGame } from "../bridge/GameBridge";

let bound = false;

/** js/ui.js:296-300 的 isInputElement 判定 */
function isInputTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  const type = (target as HTMLInputElement).type;
  return (
    tag === "TEXTAREA" ||
    (tag === "INPUT" &&
      (type === "text" || type === "number" || type === "password" || type === "email"))
  );
}

function onKeyUp(event: KeyboardEvent): void {
  const game = getGame();
  if (!game) {
    return;
  }
  if (isInputTarget(event.target)) {
    return;
  }

  const keyCode = event.keyCode;
  const isTabNumber = (keyCode >= 48 && keyCode <= 57) || (keyCode >= 96 && keyCode <= 105);

  if (isTabNumber) {
    // js/ui.js:307-317
    let tabIndex = 9;
    if (keyCode >= 97) {
      // numpad
      tabIndex = keyCode - 97;
    } else if (keyCode >= 49 && keyCode <= 57) {
      // number row
      tabIndex = keyCode - 49;
    }
    const tab = game.tabs[tabIndex] as { visible: boolean; tabId: string } | undefined;
    if (tab && tab.visible) {
      game.ui.activeTabId = tab.tabId;
      game.ui.render();
    }
  } else if (keyCode === 37 || keyCode === 39) {
    // left arrow, right arrow（js/ui.js:318-335）
    const visibleTabs: { tabId: string }[] = [];
    let activeTabIndex = 0;
    for (const tab of game.tabs as { visible: boolean; tabId: string }[]) {
      if (tab.visible) {
        if (tab.tabId === game.ui.activeTabId) {
          activeTabIndex = visibleTabs.length;
        }
        visibleTabs.push(tab);
      }
    }
    const jump = keyCode === 37 ? -1 : 1;
    const switchTab = visibleTabs[activeTabIndex + jump];
    if (switchTab) {
      game.ui.activeTabId = switchTab.tabId;
      game.ui.render();
    }
  }
}

/** 启用新层键盘切 tab；幂等（重复调用不重复绑定）。 */
export function enableTabKeys(): void {
  if (bound) {
    return;
  }
  bound = true;
  window.addEventListener("keyup", onKeyUp);
}

/** 停用并摘除监听；停用后旧层分支（守卫失效）恢复原路径。 */
export function disableTabKeys(): void {
  if (!bound) {
    return;
  }
  bound = false;
  window.removeEventListener("keyup", onKeyUp);
}

export function isTabKeysActive(): boolean {
  return bound;
}
